import { Link } from 'react-router-dom';
import { postForm } from '@/shared/api/client';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { getYearsText } from '@/shared/lib/years';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig, ListResponse, ListRow } from '@/shared/ui/list/types';

interface CryptoRequest extends ListRow {
  id: number;
  first_name?: string;
  second_name?: string;
  last_name?: string;
  birthday?: number;
  create_time?: number;
  status: { value: number; description?: string };
}

const STATUS_LABEL_CLASS: Record<number, string> = { 1: 'label-info', 2: 'label-success', 3: 'label-danger' };

const getFullYears = (birthdaySeconds?: number) => {
  if (!birthdaySeconds) return 0;
  const birth = new Date(birthdaySeconds * 1000);
  const now = new Date();
  const hadBirthday =
    now.getMonth() > birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() >= birth.getDate());
  return now.getFullYear() - birth.getFullYear() - (hadBirthday ? 0 : 1);
};

const fullName = (row: CryptoRequest) =>
  `${row.second_name ?? ''} ${row.first_name ?? ''} ${row.last_name ?? ''}`.toLowerCase();

const config: ListPageConfig<CryptoRequest> = {
  queryKey: 'crypto',
  title: 'Crypto',
  errorText: 'Ошибка загрузки заявок',
  fetchList: () => postForm<ListResponse>('/crypto/default/list/', {}),
  rows: ['items'],
  paginated: false,
  filters: [
    {
      name: 'fio',
      label: 'Поиск по ФИО',
      kind: 'text',
      placeholder: 'Поиск по ФИО...',
      clientOnly: true,
    },
  ],
  // Legacy filters by FIO and sorts by status on the client (ascending by default).
  selectRows: (rows, { applied, sortField, sortOrder }) => {
    const query = String(applied.fio).trim().toLowerCase();
    const direction = sortField === 'status' && sortOrder === 'desc' ? -1 : 1;
    return rows
      .filter((row) => fullName(row).includes(query))
      .sort((a, b) => (a.status.value - b.status.value) * direction);
  },
  columns: [
    { header: '#', render: (row) => row.id },
    { header: 'Фамилия', render: (row) => row.second_name },
    { header: 'Имя', render: (row) => row.first_name },
    { header: 'Отчество', render: (row) => row.last_name },
    {
      header: 'Кол-во полных лет',
      render: (row) => {
        const years = getFullYears(row.birthday);
        return `${years} ${getYearsText(years)}`;
      },
    },
    { header: 'Дата заявки', render: (row) => formatUnixDateTime(row.create_time) },
    {
      header: 'Статус',
      sortField: 'status',
      render: ({ status }) => (
        <span className={`label ${STATUS_LABEL_CLASS[status.value] ?? 'label-default'}`}>
          {STATUS_LABEL_CLASS[status.value]
            ? status.description
            : `Статус неопределен: ${status.value}`}
        </span>
      ),
    },
    {
      header: 'Просмотр',
      render: (row) => (
        <Link to={`/crypto/${row.id}`} className="btn btn-info">
          Просмотр
        </Link>
      ),
    },
  ],
};

export const CryptoPage = () => <GenericListPage config={config} />;
